import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  GraduationCap, ClipboardList, IndianRupee, AlertCircle, CalendarClock,
  Loader2, ArrowUpRight, Info, PhoneCall, TrendingDown, CalendarRange, Bell,
  CreditCard, AlertTriangle, UserCog, Users, CheckCircle2,
} from "lucide-react";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const NOTIF_ICON = {
  fee_overdue: { icon: AlertTriangle, tint: "text-red-600 bg-red-100" },
  fee_due: { icon: AlertTriangle, tint: "text-amber-600 bg-amber-100" },
  payment: { icon: CreditCard, tint: "text-accent bg-accent/10" },
  enrollment: { icon: ClipboardList, tint: "text-primary bg-primary/10" },
  batch_assigned: { icon: Users, tint: "text-sky-600 bg-sky-100" },
  trainer_assigned: { icon: UserCog, tint: "text-violet-600 bg-violet-100" },
  batch_soon: { icon: CalendarClock, tint: "text-indigo-600 bg-indigo-100" },
  course_completed: { icon: CheckCircle2, tint: "text-emerald-600 bg-emerald-100" },
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function StatCard({ icon: Icon, label, value, tint, testid, foot, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="rounded-xl border border-border bg-card p-5 text-left transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-md"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${tint}`}>
          <Icon className="h-[18px] w-[18px]" />
        </span>
      </div>
      <p className="mt-3 font-display text-2xl font-bold tracking-tight text-secondary">{value}</p>
      {foot && <p className="mt-1 text-xs text-muted-foreground">{foot}</p>}
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [collections, setCollections] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: col }, { data: nt }] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/collections"),
          api.get("/notifications", { params: { limit: 6 } }),
        ]);
        setStats(data);
        setCollections(col);
        setNotifs(nt.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  const k = stats.kpis;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle="Your enrollment & training operations at a glance."
      />

      {/* Operations KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard testid="card-total-students" icon={GraduationCap} label="Total Students" value={k.total_students} tint="bg-violet-500/10 text-violet-600" foot="Enrolled students" onClick={() => navigate("/students")} />
        <StatCard testid="card-new-enrollments" icon={ClipboardList} label="New Enrollments" value={k.new_enrollments} tint="bg-primary/10 text-primary" foot="This month" onClick={() => navigate("/enrollments")} />
        <StatCard testid="card-todays-collections" icon={IndianRupee} label="Today's Collections" value={fmtMoney(collections?.today_collected)} tint="bg-accent/10 text-accent" foot="Received today" onClick={() => navigate("/payments")} />
        <StatCard testid="card-pending-fees" icon={AlertCircle} label="Pending Fees" value={fmtMoney(k.pending_fees)} tint="bg-rose-500/10 text-rose-600" foot="Outstanding balance" onClick={() => navigate("/payments")} />
        <StatCard testid="card-upcoming-payments" icon={CalendarClock} label="Upcoming Due Payments" value={fmtMoney(k.upcoming_payments)} tint="bg-amber-500/10 text-amber-600" foot={`${k.upcoming_payments_count} due soon`} onClick={() => navigate("/enrollments")} />
        <StatCard testid="card-running-batches" icon={CalendarRange} label="Running Batches" value={k.running_batches} tint="bg-sky-500/10 text-sky-600" foot="Ongoing batches" onClick={() => navigate("/batches")} />
      </div>

      {/* Collections + chase list */}
      {collections && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="collections-widget">
          <div className="rounded-xl border border-border bg-card p-6 lg:col-span-1">
            <h3 className="font-display text-lg font-semibold text-secondary">Collections</h3>
            <p className="mb-4 text-sm text-muted-foreground">Fee inflow at a glance.</p>
            <div className="space-y-3">
              {[
                { icon: IndianRupee, label: "Collected today", value: collections.today_collected, tint: "text-accent" },
                { icon: IndianRupee, label: "Collected this month", value: collections.month_collected, tint: "text-primary" },
                { icon: AlertCircle, label: "Total pending", value: collections.total_pending, tint: "text-rose-600" },
                { icon: TrendingDown, label: "Overdue", value: collections.overdue_amount, tint: "text-amber-600" },
              ].map((r) => (
                <div key={r.label} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span className="flex items-center gap-2 text-sm text-slate-600"><r.icon className={`h-4 w-4 ${r.tint}`} />{r.label}</span>
                  <span className="font-display font-bold text-secondary">{fmtMoney(r.value)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2" data-testid="top-dues">
            <div className="mb-4 flex items-center gap-2">
              <PhoneCall className="h-4 w-4 text-primary" />
              <h3 className="font-display text-lg font-semibold text-secondary">Students to follow up for fees</h3>
            </div>
            {collections.top_dues.length === 0 ? (
              <div className="flex h-[220px] flex-col items-center justify-center gap-2 text-center text-muted-foreground"><Info className="h-6 w-6" /><p className="text-sm">No pending dues. All collected! 🎉</p></div>
            ) : (
              <div className="divide-y divide-border">
                {collections.top_dues.map((d) => (
                  <div key={d.enrollment_code} className="flex items-center justify-between py-3" data-testid={`due-row-${d.enrollment_code}`}>
                    <div>
                      <p className="font-medium text-secondary">{d.student_name}</p>
                      <p className="text-xs text-muted-foreground">{d.enrollment_code} · {d.course_name} {d.next_due_date ? `· due ${d.next_due_date}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${d.payment_status === "Overdue" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{d.payment_status}</span>
                      <span className="font-display font-bold text-secondary">{fmtMoney(d.pending_amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Revenue trend + recent notifications */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2" data-testid="chart-revenue">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-secondary">Collections Trend</h3>
              <p className="text-sm text-muted-foreground">Fees collected over the last 6 months.</p>
            </div>
            <span className="flex items-center gap-1 rounded-full bg-accent/10 px-2.5 py-1 text-xs font-medium text-green-700">
              <ArrowUpRight className="h-3.5 w-3.5" /> Live data
            </span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stats.revenue_trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-6" data-testid="recent-notifications">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              <h3 className="font-display text-lg font-semibold text-secondary">Recent Notifications</h3>
            </div>
            <button onClick={() => navigate("/notifications")} className="text-xs font-medium text-primary hover:underline" data-testid="view-all-notifications">View all</button>
          </div>
          {notifs.length === 0 ? (
            <div className="flex h-[240px] flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <Info className="h-6 w-6" />
              <p className="max-w-[200px] text-sm">No recent activity yet.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {notifs.map((n) => {
                const cfg = NOTIF_ICON[n.type] || NOTIF_ICON.enrollment;
                const Icon = cfg.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => n.link && navigate(n.link)}
                    data-testid={`dash-notif-${n.id}`}
                    className="flex w-full items-start gap-2.5 rounded-lg px-1.5 py-2 text-left transition-colors hover:bg-slate-50"
                  >
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${cfg.tint}`}><Icon className="h-4 w-4" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-secondary">{n.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">{n.message}</span>
                      <span className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
